import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, Mail, MapPin, Phone, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INQUIRY_TYPES = [
  "Institutional Investment",
  "Family Office",
  "Strategic Partnership",
  "Media",
  "General",
];

const initialState = {
  name: "",
  email: "",
  organization: "",
  inquiry_type: "General",
  message: "",
};

// Extracted so it's independently testable and doesn't add to the component's complexity.
const validateContactForm = (form) => {
  if (!form.name.trim()) return "Please enter your name.";
  if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Please enter a valid email.";
  if (form.message.trim().length < 10) return "Please share a bit more about your inquiry (10+ characters).";
  return null;
};

/* ============================== FIELD ============================== */
const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="label-zt text-navy/50">
      {label} {required && <span className="text-gold-600">*</span>}
    </span>
    <div className="mt-3">{children}</div>
  </label>
);

/* ============================== PAGE HEADER ============================== */
const ContactHeader = () => (
  <section
    className="bg-ivory text-navy pt-44 pb-24 md:pt-52 md:pb-32 relative overflow-hidden border-b border-navy/10"
    data-testid="contact-header"
  >
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(200,169,97,0.10),transparent_55%)]" />
    <div className="container-zt relative">
      <Reveal>
        <p className="label-zt text-gold-600">Contact</p>
        <div className="h-px w-12 bg-gold-600 mt-3" />
      </Reveal>
      <Reveal delay={120}>
        <h1 className="mt-8 font-serif text-5xl md:text-7xl leading-[1.02] tracking-tight max-w-4xl text-navy">
          Get in <span className="italic text-gold-600">Touch</span>.
        </h1>
      </Reveal>
      <Reveal delay={240}>
        <p className="mt-8 max-w-2xl text-lg text-navy/70 font-light leading-relaxed">
          For partnership, capital, or general inquiries — we'd be glad to hear from
          you. A member of the team will respond personally.
        </p>
      </Reveal>
    </div>
  </section>
);

/* ============================== SUCCESS PANEL ============================== */
const ContactSuccess = ({ onSendAnother }) => (
  <Reveal>
    <div
      className="mt-12 border border-gold/40 bg-white/60 p-10 md:p-14"
      data-testid="contact-success"
    >
      <CheckCircle2 className="text-gold-600" size={36} strokeWidth={1.2} />
      <h2 className="mt-6 font-serif text-3xl md:text-4xl text-navy tracking-tight">
        Thank you.
      </h2>
      <p className="mt-4 text-base text-navy/70 leading-relaxed font-light max-w-md">
        Your inquiry has been received. A member of the ZT Global team will
        respond to you personally at the earliest opportunity.
      </p>
      <button
        type="button"
        onClick={onSendAnother}
        className="mt-10 btn-outline-dark"
        data-testid="contact-send-another"
      >
        Send another inquiry
      </button>
    </div>
  </Reveal>
);

/* ============================== FORM ============================== */
const ContactForm = ({ form, submitting, onUpdate, onSubmit }) => (
  <form
    onSubmit={onSubmit}
    className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8"
    data-testid="contact-form"
    noValidate
  >
    <Field label="Name" required>
      <input
        type="text"
        className="input-zt"
        value={form.name}
        onChange={onUpdate("name")}
        placeholder="Full name"
        data-testid="contact-input-name"
        required
      />
    </Field>
    <Field label="Email" required>
      <input
        type="email"
        className="input-zt"
        value={form.email}
        onChange={onUpdate("email")}
        placeholder="you@firm.com"
        data-testid="contact-input-email"
        required
      />
    </Field>
    <Field label="Organization">
      <input
        type="text"
        className="input-zt"
        value={form.organization}
        onChange={onUpdate("organization")}
        placeholder="Firm or family office"
        data-testid="contact-input-organization"
      />
    </Field>
    <Field label="Inquiry Type">
      <select
        className="input-zt appearance-none bg-transparent"
        value={form.inquiry_type}
        onChange={onUpdate("inquiry_type")}
        data-testid="contact-select-inquiry-type"
      >
        {INQUIRY_TYPES.map((t) => (
          <option key={t} value={t}>{t}</option>
        ))}
      </select>
    </Field>
    <div className="md:col-span-2">
      <Field label="Message" required>
        <textarea
          rows={5}
          className="input-zt resize-none"
          value={form.message}
          onChange={onUpdate("message")}
          placeholder="Briefly describe your interest or inquiry…"
          data-testid="contact-input-message"
          required
        />
      </Field>
    </div>
    <div className="md:col-span-2 flex items-center justify-between gap-6 pt-4">
      <p className="text-[11px] tracking-[0.2em] uppercase text-navy/40">
        Your information is kept strictly confidential.
      </p>
      <button
        type="submit"
        disabled={submitting}
        className="btn-navy disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid="contact-submit"
      >
        {submitting ? "Sending…" : "Submit Inquiry"} <ArrowRight size={14} />
      </button>
    </div>
  </form>
);

/* ============================== SIDEBAR INFO ============================== */
const ContactInfo = () => (
  <aside className="md:col-span-5 md:border-l md:border-navy/10 md:pl-16">
    <Reveal>
      <p className="label-zt text-gold-600">Direct</p>
      <div className="hairline-gold mt-3" />
    </Reveal>
    <Reveal delay={120}>
      <h2 className="mt-8 font-serif text-3xl md:text-4xl text-navy tracking-tight">
        Reach us <span className="italic">directly</span>.
      </h2>
      <ul className="mt-10 space-y-8">
        <li className="flex items-start gap-4">
          <Mail size={18} className="mt-1 text-gold-600" strokeWidth={1.2} />
          <div>
            <p className="label-zt text-navy/50">Email</p>
            <a
              href="mailto:info@ztglobal.com"
              className="mt-2 block font-serif text-2xl text-navy hover:text-gold-600 transition-colors"
              data-testid="contact-info-email"
            >
              info@ztglobal.com
            </a>
          </div>
        </li>
        <li className="flex items-start gap-4">
          <Phone size={18} className="mt-1 text-gold-600" strokeWidth={1.2} />
          <div>
            <p className="label-zt text-navy/50">Phone</p>
            <a
              href="tel:+97144019490"
              className="mt-2 block font-serif text-2xl text-navy hover:text-gold-600 transition-colors"
              data-testid="contact-info-phone"
            >
              +971 4 40 19490
            </a>
          </div>
        </li>
        <li className="flex items-start gap-4">
          <MapPin size={18} className="mt-1 text-gold-600" strokeWidth={1.2} />
          <div>
            <p className="label-zt text-navy/50">Office</p>
            <p className="mt-2 font-serif text-2xl text-navy leading-snug">
              Level 1, DIFC Funds Centre<br />Precinct Building 4
            </p>
            <p className="mt-2 text-sm text-navy/55">
              Dubai International Financial Centre, Dubai, UAE
            </p>
          </div>
        </li>
      </ul>
    </Reveal>

    <Reveal delay={220}>
      <div className="mt-14 border-t border-navy/10 pt-8">
        <p className="label-zt text-gold-600">Regulatory Status</p>
        <p className="mt-4 text-sm text-navy/65 leading-relaxed font-light">
          ZT Global is a DIFC-licensed entity, regulated by the Dubai Financial
          Services Authority (DFSA). All capital structuring, advisory and
          investment activity is conducted in compliance with its DIFC licence
          and applicable DFSA regulations.
        </p>
      </div>
    </Reveal>
  </aside>
);

/* ============================== CONTACT ============================== */
const Contact = () => {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validateContactForm(form);
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/contact`, {
        ...form,
        organization: form.organization || null,
      });
      if (data?.success) {
        setSubmitted(true);
        setForm(initialState);
        toast.success("Thank you. Your inquiry has been received.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (e2) {
      const msg = e2?.response?.data?.detail || "Could not send. Please try again later.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <ContactHeader />

      <section className="bg-ivory py-24 md:py-32" data-testid="contact-body">
        <div className="container-zt grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
          <div className="md:col-span-7">
            <Reveal>
              <p className="label-zt text-gold-600">Inquiry Form</p>
              <div className="hairline-gold mt-3" />
            </Reveal>

            {submitted ? (
              <ContactSuccess onSendAnother={() => setSubmitted(false)} />
            ) : (
              <ContactForm
                form={form}
                submitting={submitting}
                onUpdate={update}
                onSubmit={onSubmit}
              />
            )}
          </div>

          <ContactInfo />
        </div>
      </section>
    </>
  );
};

export default Contact;
